import React, { useRef, useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Loader2, PenLine, Type as TypeIcon, Eraser } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Reusable dialog that drives the /api/client-documents/{id}/acknowledge endpoint.
 *
 * Required action types: read | signed | approved | completed | rejected
 *
 * Props:
 *   open, onOpenChange      — dialog control
 *   doc                     — { id, file_name, requires_action, ... }
 *   onSuccess(ackResp)      — called after a successful acknowledgment
 *   actionOverride          — optional, otherwise uses doc.requires_action
 *   currentUserName         — used to pre-fill typed signature
 */
const ACTION_LABELS = {
  read: { title: "Mark as Read", verb: "Mark Read", needsSig: false, tone: "default" },
  signed: { title: "Sign & Submit", verb: "Sign & Submit", needsSig: true, tone: "default" },
  approved: { title: "Approve Document", verb: "Approve", needsSig: true, tone: "default" },
  completed: { title: "Mark as Completed", verb: "Mark Completed", needsSig: false, tone: "default" },
  rejected: { title: "Reject Document", verb: "Reject", needsSig: false, tone: "destructive" },
};

const DocumentSignOffDialog = ({
  open,
  onOpenChange,
  doc,
  onSuccess,
  actionOverride,
  currentUserName = "",
}) => {
  const action = actionOverride || doc?.requires_action || "signed";
  const meta = ACTION_LABELS[action] || ACTION_LABELS.signed;

  const [mode, setMode] = useState("typed");  // typed | draw
  const [typedName, setTypedName] = useState(currentUserName || "");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const canvasRef = useRef(null);
  const drawingRef = useRef(false);

  // Reset state every time the dialog opens
  useEffect(() => {
    if (open) {
      setTypedName(currentUserName || "");
      setNotes("");
      setMode("typed");
      setSubmitting(false);
      // Clear canvas if mounted later
      setTimeout(() => clearCanvas(), 50);
    }
  }, [open, currentUserName]);

  const getCanvasCtx = () => {
    const c = canvasRef.current;
    if (!c) return null;
    return c.getContext("2d");
  };

  const startDraw = (e) => {
    drawingRef.current = true;
    const ctx = getCanvasCtx();
    if (!ctx) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#0f172a";
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const moveDraw = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const ctx = getCanvasCtx();
    if (!ctx) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const endDraw = () => {
    drawingRef.current = false;
  };

  const clearCanvas = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
  };

  const buildSignaturePayload = () => {
    if (!meta.needsSig) return null;
    if (mode === "draw") {
      const c = canvasRef.current;
      if (!c) return null;
      // Detect if canvas is empty
      const ctx = c.getContext("2d");
      const px = ctx.getImageData(0, 0, c.width, c.height).data;
      let hasInk = false;
      for (let i = 3; i < px.length; i += 4) {
        if (px[i] > 0) { hasInk = true; break; }
      }
      if (!hasInk) return null;
      return c.toDataURL("image/png");
    }
    return typedName.trim() ? `typed:${typedName.trim()}` : null;
  };

  const handleSubmit = async () => {
    if (!doc) return;
    const sig = buildSignaturePayload();
    if (meta.needsSig && !sig) {
      toast.error(mode === "draw" ? "Please draw your signature." : "Please type your full name.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/client-documents/${doc.id}/acknowledge`, {
        action,
        signature_data: sig,
        notes: notes || undefined,
      });
      toast.success(`Document ${action} successfully.`);
      onOpenChange(false);
      if (onSuccess) onSuccess(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to record acknowledgment");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="signoff-dialog">
        <DialogHeader>
          <DialogTitle>{meta.title}</DialogTitle>
          <DialogDescription>
            {doc?.file_name ? <span className="font-medium">{doc.file_name}</span> : null}
            <span className="block text-xs text-muted-foreground mt-1">
              Your IP address, browser, and timestamp will be captured as legal proof of consent.
            </span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {meta.needsSig && (
            <div className="space-y-2">
              <Label>Signature</Label>
              <Tabs value={mode} onValueChange={setMode}>
                <TabsList className="grid grid-cols-2 w-full">
                  <TabsTrigger value="typed" data-testid="signoff-tab-typed">
                    <TypeIcon className="h-3.5 w-3.5 mr-1" /> Type
                  </TabsTrigger>
                  <TabsTrigger value="draw" data-testid="signoff-tab-draw">
                    <PenLine className="h-3.5 w-3.5 mr-1" /> Draw
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="typed" className="pt-3">
                  <Input
                    value={typedName}
                    onChange={(e) => setTypedName(e.target.value)}
                    placeholder="Type your full legal name"
                    data-testid="signoff-typed-name"
                    autoFocus
                  />
                  {typedName && (
                    <p className="mt-2 px-3 py-2 border rounded bg-muted/40 text-2xl font-serif italic">
                      {typedName}
                    </p>
                  )}
                </TabsContent>
                <TabsContent value="draw" className="pt-3 space-y-2">
                  <div className="border rounded bg-white">
                    <canvas
                      ref={canvasRef}
                      width={420}
                      height={120}
                      className="w-full touch-none rounded"
                      onMouseDown={startDraw}
                      onMouseMove={moveDraw}
                      onMouseUp={endDraw}
                      onMouseLeave={endDraw}
                      onTouchStart={startDraw}
                      onTouchMove={moveDraw}
                      onTouchEnd={endDraw}
                      data-testid="signoff-canvas"
                    />
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    type="button"
                    onClick={clearCanvas}
                    data-testid="signoff-clear-canvas"
                  >
                    <Eraser className="h-3.5 w-3.5 mr-1" /> Clear
                  </Button>
                </TabsContent>
              </Tabs>
            </div>
          )}

          <div className="space-y-2">
            <Label>Notes (optional)</Label>
            <Textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add a comment for the sender..."
              data-testid="signoff-notes"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            data-testid="signoff-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            variant={meta.tone === "destructive" ? "destructive" : "default"}
            data-testid="signoff-submit"
          >
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {meta.verb}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default DocumentSignOffDialog;
